import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import api from '@/lib/api';
import { Sparkles, Zap, DollarSign, Clock, BarChart3, RefreshCw, CheckCircle, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';

const taskLabels = {
  product_title_seo: 'Title SEO',
  product_description: 'Description',
  bulk_product_optimization: 'Bulk Optimize',
  homepage_copy: 'Homepage Copy',
  brand_identity: 'Brand Identity',
  chat_assistant: 'Chat',
  custom_edit: 'Custom Edit',
  translation: 'Translation',
};

export default function AIUsagePage() {
  const [usage, setUsage] = useState(null);
  const [logs, setLogs] = useState([]);
  const [logTotal, setLogTotal] = useState(0);
  const [logPage, setLogPage] = useState(1);
  const [taskFilter, setTaskFilter] = useState('');
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: logPage, limit: 20 });
      if (taskFilter) params.set('task_type', taskFilter);
      const [u, l] = await Promise.all([api.get('/ai/usage'), api.get(`/ai/logs?${params}`)]);
      setUsage(u.data);
      setLogs(l.data.logs || []);
      setLogTotal(l.data.total || 0);
    } catch {}
    setLoading(false);
  }, [logPage, taskFilter]);

  useEffect(() => { fetch(); }, [fetch]);

  if (loading && !usage) return <div className="p-12 text-center font-sans text-sm" style={{ color: 'rgba(26,26,26,0.4)' }}>Loading...</div>;

  return (
    <motion.div data-testid="ai-usage-page" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-serif text-2xl tracking-tight" style={{ color: '#1A1A1A' }}>AI Usage</h1>
          <p className="font-sans text-sm mt-1" style={{ color: 'rgba(26,26,26,0.5)' }}>{usage?.month || 'This month'}</p>
        </div>
        <div className="flex items-center gap-2">
          {usage?.ai_configured ? (
            <Badge className="rounded-sm text-[10px]" style={{ backgroundColor: 'rgba(34,197,94,0.1)', color: '#16a34a', border: 'none' }}>OpenRouter Connected</Badge>
          ) : (
            <Badge className="rounded-sm text-[10px]" style={{ backgroundColor: 'rgba(234,179,8,0.1)', color: '#ca8a04', border: 'none' }}>Not Configured</Badge>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
        {[
          { label: 'Total Calls', value: usage?.total_calls || 0, icon: Zap, color: '#E85D3A' },
          { label: 'Successful', value: usage?.success_calls || 0, icon: CheckCircle, color: '#16a34a' },
          { label: 'Total Tokens', value: (usage?.total_tokens || 0).toLocaleString(), icon: BarChart3, color: '#2563eb' },
          { label: 'Est. Cost', value: `$${(usage?.estimated_cost || 0).toFixed(4)}`, icon: DollarSign, color: '#C4956A' },
        ].map(({ label, value, icon: Icon, color }) => (
          <div key={label} className="p-4 rounded-lg border" style={{ borderColor: 'rgba(26,26,26,0.08)', backgroundColor: '#FDFBF7' }}>
            <div className="flex items-center gap-2 mb-2">
              <Icon size={14} style={{ color }} />
              <span className="font-sans text-xs uppercase tracking-wider" style={{ color: 'rgba(26,26,26,0.4)' }}>{label}</span>
            </div>
            <p className="font-sans text-xl font-medium" style={{ color: '#1A1A1A' }}>{value}</p>
          </div>
        ))}
      </div>

      {/* By Task Type */}
      {usage?.by_task_type && Object.keys(usage.by_task_type).length > 0 && (
        <div className="mb-8">
          <h3 className="font-sans text-xs uppercase tracking-wider font-medium mb-4" style={{ color: '#C4956A' }}>By Task Type</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {Object.entries(usage.by_task_type).map(([type, data]) => (
              <div key={type} className="p-3 rounded-lg border" style={{ borderColor: 'rgba(26,26,26,0.08)', backgroundColor: '#FDFBF7' }}>
                <p className="font-sans text-xs font-medium mb-1" style={{ color: '#1A1A1A' }}>{taskLabels[type] || type}</p>
                <p className="font-sans text-sm" style={{ color: 'rgba(26,26,26,0.6)' }}>{data.calls} calls &middot; {data.tokens.toLocaleString()} tokens</p>
                <p className="font-sans text-[10px]" style={{ color: 'rgba(26,26,26,0.35)' }}>Avg {data.avg_duration}ms</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Logs */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-sans text-xs uppercase tracking-wider font-medium" style={{ color: '#C4956A' }}>AI Logs ({logTotal})</h3>
          <div className="flex gap-2">
            <Select value={taskFilter || 'all'} onValueChange={v => { setTaskFilter(v === 'all' ? '' : v); setLogPage(1); }}>
              <SelectTrigger className="w-[140px] font-sans text-xs rounded-none h-8" style={{ borderColor: 'rgba(26,26,26,0.12)' }}>
                <SelectValue placeholder="All Tasks" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Tasks</SelectItem>
                {Object.entries(taskLabels).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={fetch} className="rounded-none font-sans text-xs gap-1 h-8" style={{ borderColor: 'rgba(26,26,26,0.12)' }}>
              <RefreshCw size={12} /> Refresh
            </Button>
          </div>
        </div>

        <div className="rounded-lg border overflow-hidden" style={{ borderColor: 'rgba(26,26,26,0.08)', backgroundColor: '#FDFBF7' }}>
          <table className="w-full">
            <thead><tr style={{ backgroundColor: 'rgba(26,26,26,0.02)' }}>
              {['Status', 'Task', 'Model', 'Tokens', 'Duration', 'Preview', 'Date'].map(h => (
                <th key={h} className="text-left p-2.5 font-sans text-[10px] uppercase tracking-wider" style={{ color: 'rgba(26,26,26,0.4)' }}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {logs.length === 0 ? (
                <tr><td colSpan={7} className="p-12 text-center">
                  <Sparkles size={28} className="mx-auto mb-3" style={{ color: 'rgba(26,26,26,0.15)' }} />
                  <p className="font-sans text-sm" style={{ color: 'rgba(26,26,26,0.4)' }}>No AI calls yet</p>
                </td></tr>
              ) : logs.map((l, i) => (
                <tr key={i} className="border-t hover:bg-[rgba(26,26,26,0.01)]" style={{ borderColor: 'rgba(26,26,26,0.06)' }}>
                  <td className="p-2.5">
                    {l.status === 'success' ? <CheckCircle size={14} style={{ color: '#16a34a' }} /> : <XCircle size={14} style={{ color: '#dc2626' }} />}
                  </td>
                  <td className="p-2.5 font-sans text-xs" style={{ color: '#1A1A1A' }}>{taskLabels[l.task_type] || l.task_type}</td>
                  <td className="p-2.5 font-mono text-[10px]" style={{ color: 'rgba(26,26,26,0.5)' }}>{l.model?.split('/').pop()}</td>
                  <td className="p-2.5 font-sans text-xs" style={{ color: 'rgba(26,26,26,0.6)' }}>{l.tokens_total?.toLocaleString()}</td>
                  <td className="p-2.5 font-sans text-xs" style={{ color: 'rgba(26,26,26,0.6)' }}>{l.duration_ms}ms</td>
                  <td className="p-2.5 font-sans text-xs max-w-[200px] truncate" style={{ color: 'rgba(26,26,26,0.45)' }}>{l.response_preview}</td>
                  <td className="p-2.5 font-sans text-[10px]" style={{ color: 'rgba(26,26,26,0.35)' }}>{new Date(l.created_at).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {logTotal > 20 && (
          <div className="flex items-center justify-between mt-4">
            <p className="font-sans text-xs" style={{ color: 'rgba(26,26,26,0.4)' }}>Page {logPage}</p>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={logPage <= 1} onClick={() => setLogPage(p => p - 1)} className="font-sans text-xs rounded-none h-7">Prev</Button>
              <Button variant="outline" size="sm" disabled={logPage >= Math.ceil(logTotal / 20)} onClick={() => setLogPage(p => p + 1)} className="font-sans text-xs rounded-none h-7">Next</Button>
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
}
